import { useContext, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, Plus, Sparkles, X } from 'lucide-react'
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json'
import { useBuilder } from './hooks/useBuilder'
import { BuilderBlockMount } from './BuilderBlockMount'
import { BuilderBlockWrapper } from './BuilderBlockWrapper'
import { BuilderBlockCatalog, DASHBOARD_CATEGORY, SYSTEM_CATEGORY } from './BuilderBlockCatalog'
import { peekPendingPlacements, reservedSpotsFor } from './blockPlacement'
import { useChatTurns, liveConversationIds } from './chat/turnStatus'
import { BuilderBlockSkeleton } from './BuilderBlockSkeleton'
import { BLOCK_TARGET_EVENT, buildBlockContext } from './chat/blockContext'
import { BlockSettingsMenu, CELL_CHROME_TRIGGER } from './BlockSettingsMenu'
import { useCellSettings } from './hooks/useCellSettings'

/**
 * ColumnsLayout — le moteur unique des GABARITS EN COLONNES. Un moteur, trois
 * presets déclarés en YAML (`LayoutColumnLeft`, `LayoutColumnRight`,
 * `LayoutColumnBoth`) : ils ne diffèrent que par la liste `columns:`.
 *
 * C'est le pendant « flux » du moteur `GridLayout` (grille 2-D, positions
 * absolues) : ici chaque colonne est une PILE verticale ordinaire — ce qu'on
 * attend d'une colonne latérale, dont la hauteur suit son contenu.
 *
 * Persistance : rigoureusement le patron existant. Les blocs enfants vivent
 * dans les données de CE bloc sous `cells`
 * (`[{ id, type, data, column }]`, comme un champ `list`), et toute mutation
 * (ajout / retrait / déplacement) réécrit le tableau en entier via
 * `updateBlock(blockId, 'cells', cells)` — le MÊME appel que l'éditeur de
 * liste. Les deux voies de sauvegarde sérialisent `bloc.data` en bloc : `cells`
 * persiste sans machinerie neuve.
 *
 * L'ORDRE dans une colonne est celui du tableau global : une cellule ajoutée
 * est poussée à la fin (donc en bas de sa colonne), et « monter / descendre »
 * échange deux cellules VOISINES DANS LA COLONNE, pas dans le tableau.
 */
const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)

// Constantes PARTAGÉES : `BuilderBlockMount` mémorise son `dataOverride` sur
// les champs du bloc, et un `dataOverride` neuf RÉ-AMORCE le store de l'enfant.
// Un littéral `[]`/`{}` recréé à chaque rendu ré-amorcerait donc chaque cellule
// en permanence. Une cellule n'a pas de visibilité par appareil : c'est bien
// une constante. (Même raison que dans `GridLayout`.)
const NO_HIDDEN = []
const NO_DATA = {}

/**
 * Normalise la déclaration `columns:` de la déf. Chaque entrée vaut
 * `{ key, label, width }` — `width` en douzièmes, comme la grille, pour que les
 * deux moteurs se raisonnent dans la même unité.
 */
const normalizeColumns = (raw) => {
  const list = Array.isArray(raw) ? raw : []
  const cols = list
    .map((c, i) => ({
      key: String(c?.key ?? `col${i + 1}`),
      label: String(c?.label ?? `Colonne ${i + 1}`),
      width: Number.isFinite(Number(c?.width)) ? Math.max(1, Number(c.width)) : 6,
    }))
    // Une clé en double rendrait deux colonnes strictement identiques (même
    // filtre) : on garde la première.
    .filter((c, i, all) => all.findIndex(o => o.key === c.key) === i)
  return cols.length ? cols : [{ key: 'main', label: 'Contenu principal', width: 12 }]
}

export const ColumnsLayout = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const { page, registry, isEditMode, updateBlock, host } = useBuilder()
  // La PLACE qui attend un contenu : `{ column, at }`, où `at` est le rang visé
  // DANS la colonne (`Infinity` = à la suite). Ouvre le catalogue, qui rend le
  // bloc choisi à cette place exacte — sans quoi on ne pourrait qu'empiler par
  // le bas puis remonter le bloc à coups de chevrons.
  const [target, setTarget] = useState(null)

  // Ce gabarit EST un bloc de premier niveau ; son id donne accès à l'état de page.
  const blockId = resolve(props.blockIdLocation ?? '~~.apiData.data.objectData.builderId')
  const bloc = page.blocs.find((b) => b.id === blockId)
  const cells = Array.isArray(bloc?.data?.cells) ? bloc.data.cells : []

  const columns = normalizeColumns(resolve(props.columns))
  const gap = Number.isFinite(Number(resolve(props.gap))) ? Number(resolve(props.gap)) : 24

  const registryByType = useMemo(() => {
    const m = {}
    for (const e of registry) if (e.type) m[e.type] = e
    return m
  }, [registry])

  const setCells = (next) => { if (blockId) updateBlock(blockId, 'cells', next) }

  // Une cellule dont la colonne n'existe plus (déf modifiée, gabarit changé)
  // retombe dans la PREMIÈRE colonne plutôt que de disparaître en silence.
  const known = new Set(columns.map(c => c.key))
  const columnOf = (cell) => (known.has(cell?.column) ? cell.column : columns[0].key)
  const indexesOf = (key) => cells.reduce((acc, c, i) => (columnOf(c) === key ? [...acc, i] : acc), [])

  const removeCell = (i) => setCells(cells.filter((_, idx) => idx !== i))

  // Replie l'édition d'un champ de l'enfant dans sa cellule (même repli que la grille).
  const foldIntoCell = (i, field, value) =>
    setCells(cells.map((c, idx) => (idx === i ? { ...c, data: { ...c.data, [field]: value } } : c)))

  /** Échange la cellule avec sa voisine DANS SA COLONNE (dir : -1 monte, +1 descend). */
  const moveCell = (i, dir) => {
    const siblings = indexesOf(columnOf(cells[i]))
    const at = siblings.indexOf(i)
    const swap = siblings[at + dir]
    if (swap === undefined) return
    const next = cells.slice()
    ;[next[i], next[swap]] = [next[swap], next[i]]
    setCells(next)
  }

  // Le chemin de l'assistant pose la cellule BIEN APRÈS le clic qui l'a armé :
  // il ne doit pas écrire à travers une fermeture qui aurait capturé `cells` au
  // moment du catalogue (le gabarit a pu être édité entre-temps, ces éditions
  // seraient perdues). Miroirs tenus à jour au rendu, lus à la pose.
  const cellsRef = useRef(cells)
  cellsRef.current = cells
  // La PLACE VISÉE, elle, se capture À L'ARMEMENT — c'est ce que l'éditeur a
  // désigné. Elle vivait dans un ref unique, écrasé par la demande suivante :
  // cliquer « + » dans une seconde colonne pendant que la première se
  // fabriquait faisait atterrir les deux blocs à la même place. Une fabrique de
  // placeur par demande, et chacune garde la sienne.
  const makeCellPlacer = (place) => (blockDef) => {
    const { column, at } = place ?? { column: columns[0].key, at: Infinity }
    // Mise à jour FONCTIONNELLE : deux places demandées de front aboutissent
    // l'une après l'autre, et la seconde doit se ranger à côté de la première
    // plutôt que d'écraser un tableau calculé avant elle.
    setCells((prev) => {
      const current = Array.isArray(prev) ? prev : cellsRef.current
      // Le rang dans la colonne se traduit en index GLOBAL : l'ordre d'une colonne
      // est celui du tableau, insérer devant la n-ième sœur suffit. Les autres
      // colonnes ne bougent pas — chacune se relit par filtrage.
      const siblings = current.reduce((acc, c, i) => (columnOf(c) === column ? [...acc, i] : acc), [])
      const next = current.slice()
      next.splice(at < siblings.length ? siblings[at] : current.length, 0, {
        id: uuid(),
        type: blockDef.type,
        data: { ...(blockDef.defaultData || {}) },
        column,
      })
      return next
    })
  }

  // Les places que l'assistant est en train de remplir DANS CE GABARIT : une par
  // demande partie dont le tour tourne encore. Même repère que le carré d'attente
  // d'une page — la colonne visée, au rang visé.
  const chatTurns = useChatTurns()
  const reservedPlaces = useMemo(() => {
    if (!isEditMode || !blockId) return []
    const live = liveConversationIds(chatTurns)
    return reservedSpotsFor(peekPendingPlacements(), live, spot => spot?.kind === 'column' && spot.blockId === blockId)
      .map(spot => ({ column: spot.column, at: spot.at }))
      .filter(p => p.column)
  }, [chatTurns, isEditMode, blockId])

  /** Combien de places réservées se rangent au rang `rank` de la colonne `key`. */
  const reservedAt = (key, rank) => reservedPlaces.filter(p => (
    p.column === key && (Number.isFinite(p.at) ? p.at === rank : rank === Infinity)
  )).length

  // Réglages avancés d'un bloc de cellule : le menu vit dans le chrome de la
  // cellule, sa pop-in est celle de tous les autres champs, et l'écriture passe
  // par `foldIntoCell` comme n'importe quelle édition en place.
  const { openCellSetting, cellSettingPopover } = useCellSettings()

  // Une cellule n'a pas la barre d'outils d'un bloc de premier niveau (son
  // wrapper est `nested`) : son ✨ vit dans le chrome de la cellule — même
  // geste, même contexte d'édition épinglé qu'un bloc de premier niveau.
  const askAiForCell = (cell, i) => {
    const def = registryByType[cell.type]
    if (!def?.defNid) return
    window.dispatchEvent(new CustomEvent(BLOCK_TARGET_EVENT, {
      detail: buildBlockContext({
        bloc: { id: cell.id, type: cell.type, data: cell.data ?? {} },
        def,
        index: i,
        page,
        host: host ? { kind: 'dashboard' } : undefined,
      }),
    }))
  }

  // `--builder-columns-tpl` n'est lue qu'au-delà du point de bascule (recette
  // `.builder-columns` dans index.css) : sous cette largeur, les colonnes
  // s'empilent. La requête est une requête de CONTENEUR — le canevas
  // (`#builder-canvas`) est le conteneur, donc l'aperçu par appareil bascule
  // comme le fera le visiteur.
  const style = {
    '--builder-columns-tpl': columns.map(c => `${c.width}fr`).join(' '),
    '--builder-columns-gap': `${gap}px`,
  }

  return (
    <div className="builder-columns" style={style}>
      {columns.map((col) => {
        const indexes = indexesOf(col.key)
        return (
          <div
            key={col.key}
            className="builder-columns-col"
            // En édition, la colonne se réserve une gouttière EN HAUT ET EN BAS :
            // les liserés du premier et du dernier bloc s'y logent. Sans elles ils
            // déborderaient sur ce qui entoure le gabarit (les blocs voisins, ou
            // le bord du canevas).
            style={isEditMode ? { paddingTop: gap, paddingBottom: gap } : undefined}
          >
            {indexes.map((i, rank) => {
              const cell = cells[i]
              const def = registryByType[cell.type]
              return (
                <div key={cell.id} className="relative">
                  {isEditMode && (
                    <ColumnInsert
                      gap={gap}
                      label={`Ajouter un bloc à la section « ${col.label} », avant « ${def?.name ?? cell.type} »`}
                      onClick={() => setTarget({ column: col.key, at: rank })}
                    />
                  )}
                  {/* La place promise à l'assistant se tient là où elle a été
                      demandée, devant le bloc qui la suit — et porte son propre
                      liseré, pour enchaîner une demande sous celle qui travaille
                      sans attendre son bloc. */}
                  {Array.from({ length: reservedAt(col.key, rank) }, (_, n) => (
                    <div key={`res-${col.key}-${rank}-${n}`} className="relative">
                      <BuilderBlockSkeleton />
                      {isEditMode && (
                        <ColumnInsert
                          gap={gap}
                          placement="below"
                          label={`Ajouter un bloc à la section « ${col.label} », sous celui en cours de création`}
                          onClick={() => setTarget({ column: col.key, at: rank })}
                        />
                      )}
                    </div>
                  ))}
                  {isEditMode && (
                    <div className="absolute top-1.5 left-1.5 right-1.5 z-30 flex items-center justify-between pointer-events-none">
                      <span className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => moveCell(i, -1)}
                          disabled={rank === 0}
                          className="pointer-events-auto flex items-center justify-center w-6 h-6 rounded-md bg-white/90 border border-gray-200 text-gray-400 shadow-sm enabled:hover:text-gray-700 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                          aria-label={`Monter ce bloc dans « ${col.label} »`}
                          title="Monter"
                        >
                          <ChevronUp size={14} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveCell(i, 1)}
                          disabled={rank === indexes.length - 1}
                          className="pointer-events-auto flex items-center justify-center w-6 h-6 rounded-md bg-white/90 border border-gray-200 text-gray-400 shadow-sm enabled:hover:text-gray-700 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                          aria-label={`Descendre ce bloc dans « ${col.label} »`}
                          title="Descendre"
                        >
                          <ChevronDown size={14} aria-hidden="true" />
                        </button>
                      </span>
                      <span className="flex items-center gap-1.5">
                        {def?.settings?.length > 0 && (
                          <BlockSettingsMenu
                            settings={def.settings}
                            triggerClassName={CELL_CHROME_TRIGGER}
                            onPick={(decl, anchorEl) => openCellSetting(decl, anchorEl, {
                              value: (cell.data ?? {})[decl.field] ?? def.defaultData?.[decl.field],
                              commit: (key, value) => foldIntoCell(i, key, value),
                            })}
                          />
                        )}
                        {def?.defNid && (
                          <button
                            type="button"
                            onClick={() => askAiForCell(cell, i)}
                            className="pointer-events-auto flex items-center justify-center w-6 h-6 rounded-md bg-white/90 border border-gray-200 text-gray-400 hover:text-teal-700 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                            aria-label={`Modifier le bloc ${def?.name ?? cell.type} avec l'assistant IA`}
                            title="Modifier avec l'IA"
                          >
                            <Sparkles size={14} aria-hidden="true" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeCell(i)}
                          className="pointer-events-auto flex items-center justify-center w-6 h-6 rounded-md bg-white/90 border border-gray-200 text-gray-400 hover:text-red-600 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                          aria-label={`Retirer le bloc ${def?.name ?? cell.type} de « ${col.label} »`}
                          title="Retirer"
                        >
                          <X size={14} aria-hidden="true" />
                        </button>
                      </span>
                    </div>
                  )}

                  {def?.rjBuild ? (
                    <BuilderBlockWrapper
                      bloc={{ id: cell.id, type: cell.type, data: cell.data ?? NO_DATA, hiddenOn: NO_HIDDEN }}
                      index={i}
                      total={cells.length}
                      nested
                      onCommitField={(field, value) => foldIntoCell(i, field, value)}
                    >
                      {/* `key` sur la version de la déf : `ReactiveJsonRoot` NE
                          rejoue PAS son rjBuild quand la prop change — seul un
                          remontage prend la nouvelle définition. Même clé que
                          les blocs de premier niveau (BuilderCanvas, GridLayout). */}
                      <BuilderBlockMount
                        key={def.version}
                        bloc={{ id: cell.id, type: cell.type, data: cell.data ?? NO_DATA, hiddenOn: NO_HIDDEN }}
                        def={def}
                      />
                    </BuilderBlockWrapper>
                  ) : (
                    <div className="p-4 text-xs text-red-500 bg-red-50 border border-red-200 rounded">
                      Bloc inconnu : <code>{cell.type}</code>
                    </div>
                  )}

                  {/* Le dernier bloc porte AUSSI le liseré « à la suite » : sans
                      lui, ajouter en fin de colonne redemanderait un contrôle
                      allumé en permanence, que le rituel du builder proscrit. */}
                  {isEditMode && rank === indexes.length - 1 && (
                    <ColumnInsert
                      gap={gap}
                      placement="below"
                      label={`Ajouter un bloc à la section « ${col.label} », à la suite`}
                      onClick={() => setTarget({ column: col.key, at: Infinity })}
                    />
                  )}
                </div>
              )
            })}

            {/* Les places demandées « à la suite » de cette colonne — y compris
                quand la colonne est encore vide : c'est le seul repère que
                l'éditeur ait entre sa demande et l'arrivée du bloc. */}
            {Array.from({ length: reservedAt(col.key, Infinity) }, (_, n) => (
              <div key={`res-end-${col.key}-${n}`} className="relative">
                <BuilderBlockSkeleton />
                {isEditMode && (
                  <ColumnInsert
                    gap={gap}
                    placement="below"
                    label={`Ajouter un bloc à la section « ${col.label} », sous celui en cours de création`}
                    onClick={() => setTarget({ column: col.key, at: Infinity })}
                  />
                )}
              </div>
            ))}

            {/* Colonne VIDE : rien à survoler, donc la zone reste affichée —
                c'est le seul cas où un contrôle d'ajout est allumé en permanence,
                et il tient lieu de contenu. Dès qu'un bloc est posé, ce sont les
                liserés au survol qui prennent le relais. */}
            {isEditMode && indexes.length === 0 && (
              <button
                type="button"
                onClick={() => setTarget({ column: col.key, at: Infinity })}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 bg-gray-50/40 px-3 py-4 text-xs font-medium text-gray-500 transition-colors hover:border-teal-400 hover:bg-teal-50/70 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                aria-label={`Ajouter un bloc à la section « ${col.label} », à la suite`}
              >
                <Plus size={14} aria-hidden="true" />
                Ajouter un bloc à la section
              </button>
            )}
          </div>
        )
      })}

      {target && (
        <BuilderBlockCatalog
          position={0}
          // Ce que prend une colonne dépend de l'hôte, exactement comme une case
          // de grille : sur un tableau de bord ses widgets, sur une page les
          // blocs de la page. Jamais un conteneur (grille, gabarit en colonnes) :
          // la catégorie « system » les écarte des deux côtés — pas de gabarit
          // dans un gabarit.
          accept={(def) => (
            host
              ? def.category === DASHBOARD_CATEGORY
              : def.category !== DASHBOARD_CATEGORY && def.category !== SYSTEM_CATEGORY
          )}
          onClose={() => setTarget(null)}
          onSelect={(def) => { makeCellPlacer(target)(def); setTarget(null) }}
          // Où doit atterrir un bloc que l'assistant crée pendant ce tour. Pas
          // `onSelect` : celui-ci se ferme sur les cellules d'AUJOURD'HUI, et le
          // tour se termine bien plus tard.
          onPlace={makeCellPlacer(target)}
          // Et OÙ l'attente se voit : à la place visée, dans CETTE colonne.
          // Sans cette indication, le catalogue retombait sur la place
          // d'insertion d'une PAGE et le carré s'allumait en tête de page, loin
          // du gabarit — qui, lui, ne montrait rien.
          placementSpot={{ kind: 'column', blockId, column: target?.column, at: target?.at }}
        />
      )}

      {cellSettingPopover}
    </div>
  )
}

/**
 * Le liseré d'insertion d'une colonne : le même geste que celui du canevas
 * (`AddBlockButton`), à l'échelle d'une pile.
 *
 * Sans lui, une colonne ne se composait que par le bas : pour glisser un bloc
 * entre deux autres, il fallait l'ajouter à la fin puis le remonter d'autant de
 * clics sur le chevron. Le bouton de fin de colonne reste, il ajoute à la suite.
 *
 * L'intitulé est ÉCRIT, pas deviné : un « + » nu ne dit ni ce qu'il ajoute ni
 * où, et le liseré du canevas a déjà appris que deux ronds énigmatiques ne se
 * distinguent pas. Le trait ne court QUE sur la largeur de la section — il naît
 * du cadre de la cellule (`inset-x-0`), donc de la colonne, jamais du canevas.
 *
 * ── Non-chevauchement, les trois règles ────────────────────────────────────
 *  1. Il est posé DANS le cadre de la cellule, en absolu au-dessus d'elle : la
 *     colonne est une pile `flex` avec `gap`, un élément de flux — même haut de
 *     zéro — y ajouterait une gouttière de plus entre chaque bloc.
 *  2. Sa bande de 24 px se CENTRE sur la gouttière réelle (`gap`), au lieu de la
 *     supposer : avec une gouttière plus étroite, la pastille reste centrée et
 *     opaque (fond blanc + ombre) plutôt que de mordre en biais sur un bloc.
 *  3. `z-20`, sous les commandes de la cellule (`z-30`) : si les deux se
 *     rencontraient, ce sont monter/descendre/retirer qui gagnent le clic.
 *
 * Révélé au survol ET au focus (`group-focus-within`) : au clavier, on tabule
 * sinon dans un bouton invisible (WCAG 2.4.7).
 */
const INSERT_BAND = 24

const ColumnInsert = ({ label, gap, onClick, placement = 'above' }) => (
  <div
    className="group absolute inset-x-0 z-20 flex items-center"
    style={{
      [placement === 'below' ? 'bottom' : 'top']: -(gap / 2) - INSERT_BAND / 2,
      height: INSERT_BAND,
    }}
  >
    <div className="flex w-full items-center gap-2 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none">
      <span className="h-px flex-1 bg-teal-500/60" aria-hidden="true" />
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="inline-flex h-6 flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 text-[11px] font-medium text-teal-700 shadow-md ring-1 ring-teal-500/40 transition-colors hover:bg-teal-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
      >
        <Plus size={12} className="flex-shrink-0" aria-hidden="true" />
        Ajouter un bloc à la section
      </button>
      <span className="h-px flex-1 bg-teal-500/60" aria-hidden="true" />
    </div>
  </div>
)
